import React, { useState, useEffect } from "react";
import axios from "../Utility/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Utility/AuthContext";
import { useParams } from "react-router-dom";

import {
  Button,
  Card,
  CardContent,
  CardActions,
  CardActionArea,
  Typography,
  Alert,
  Stack,
  Container,
} from "@mui/material";

export default function Admin() {
  const { username } = useParams();
  const [stories, setStories] = useState([]);
  const description = "Review reported stories.";
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const API = process.env.REACT_APP_API_URL || "";

  // Redirect if not logged in
  useEffect(() => {
    if (!loading && (!user || !user.isAdmin)) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  // Load user's stories
  useEffect(() => {
    if (!user) return;

    axios
      .get(`${API}/api/stories/admin`, {
        withCredentials: true,
      })
      .then((res) => {
        setStories(res.data);
      })
      .catch((err) =>
        setError(err.response?.data?.error || "Failed to load stories"),
      );
  }, [user, loading, navigate, API]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this story?")) return;

    try {
      await axios.delete(`${API}/api/stories/${id}`, {
        withCredentials: true,
      });

      setStories((prev) => prev.filter((s) => s.id !== id));

      setSuccess("Story deleted successfully.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete story");
    }
  };

  const handleAllow = async (id) => {
    if (!window.confirm("Are you sure you want to allow this story?")) return;

    try {
      await axios.put(
        `${API}/api/stories/allow/${id}`,
        {},
        {
          withCredentials: true,
        },
      );

      setStories((prev) => prev.filter((s) => s.id !== id));

      setSuccess("Story allowed successfully.");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to allow story");
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" gutterBottom>
          Admin Review
        </Typography>
      </Stack>

      <Typography sx={{ mb: 2 }}>
        <strong>Username:</strong> {username ? username : user?.username}
      </Typography>

      <Typography sx={{ mb: 2 }}>
        <strong>Author Description:</strong> {description}
      </Typography>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Stories */}
      {stories.length === 0 ? (
        <Typography>No stories found.</Typography>
      ) : (
        <Stack spacing={2}>
          {stories.map((story) => (
            <Card
              key={story.id}
              sx={{
                borderRadius: 2,
                boxShadow: 2,
              }}
            >
              <CardActionArea>
                <CardContent>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    Title: {story.story_name}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Author: {story.author}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {story.story_text}
                  </Typography>
                </CardContent>
              </CardActionArea>

              <CardActions sx={{ justifyContent: "flex-end", px: 2 }}>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(story.id);
                  }}
                >
                  Delete
                </Button>

                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAllow(story.id);
                  }}
                >
                  Allow
                </Button>
              </CardActions>
            </Card>
          ))}
        </Stack>
      )}
    </Container>
  );
}
